<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useAccountingStore } from '@/stores/accounting.store'
import { useRegistryStore } from '@/stores/registry.store'

import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  data: {
    type: Object,
  },
  entityLock: {
    type: Object,
    required: true
  },
})

const emits = defineEmits(['row-saved'])

const productId = props.product.id
const productCode = props.product.code
const gridId = 'accounting-grid'

const i18n = getI18n()
const registryStore = useRegistryStore()
const accountingStore = useAccountingStore()

const { gridRef } = dataGrid.useGridRef()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productAccountingsUrl)
gridDataSource.filter(['product_ref', '=', productId])

const getFilteredTradeAccounts = (tradeAccountTypesEnum) => {
  return {
    store: accountingStore.getLocalizedTradeAccounts(),
    filter: (item) => {
      return (item.type === accountingStore.tradeAccountTypesEnum.PURCHASE_SALE || item.type === tradeAccountTypesEnum)
    },
    sort: 'code',
  }
}

const getFilteredFinancialCenters = (financialCenterTypesEnum) => {
  return {
    store: accountingStore.getLocalizedFinancialCenters(),
    filter: (item) => {
      return (item.type === accountingStore.financialCenterTypesEnum.COST_INCOME || item.type === financialCenterTypesEnum)
    },
    sort: 'code',
  }
}

const rowButtons = props.entityLock.getEntityLockButtons()

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  {
    dataField: 'intrastat_code',
    caption: i18n.t('common.intrastatCode'),
    width: '18.75rem'
  },
  {
    dataField: 'financial_center_cost_ref',
    caption: i18n.t('common.financialCenterCost'),
    lookup: dataGrid.useLookup({ dataSource: getFilteredFinancialCenters(accountingStore.financialCenterTypesEnum.COST) }),
    showInColumnChooser: false,
    width: '18.75rem'
  },
  {
    dataField: 'financial_center_income_ref',
    caption: i18n.t('common.financialCenterIncome'),
    lookup: dataGrid.useLookup({
      dataSource: getFilteredFinancialCenters(accountingStore.financialCenterTypesEnum.INCOME),
    }),
    showInColumnChooser: false,
    width: '18.75rem'
  },
  {
    dataField: 'trade_account_purchase_ref',
    caption: i18n.t('common.tradeAccountPurchase'),
    lookup: dataGrid.useLookup({ dataSource: getFilteredTradeAccounts(accountingStore.tradeAccountTypesEnum.PURCHASE) }),
    showInColumnChooser: false,
    width: '18.75rem'
  },
  {
    dataField: 'trade_account_sale_ref',
    caption: i18n.t('common.tradeAccountSale'),
    lookup: dataGrid.useLookup({
      dataSource: getFilteredTradeAccounts(accountingStore.tradeAccountTypesEnum.SALE),
    }),
    showInColumnChooser: false,
    width: '18.75rem'
  },
]

const { defaultActionTitle, defaultItemTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.accounting')} ${productCode}`
  const defaultItemTitleValue = defaultItemTitle(props.templateData?.data)
  if (defaultItemTitleValue) {
    title += ` ${defaultItemTitleValue}`
  }
  return title
}

const editing = computed(() =>
  dataGrid.useDefaultEditing(dialogTitle, {
    items: [
      {
        itemType: 'group',
        caption: i18n.t('common.mainInfo'),
        colCount: 3,
        items: [
          { dataField: 'intrastat_code' },
          { itemType: 'empty' },
          { itemType: 'empty' },
          { dataField: 'financial_center_cost_ref' },
          { dataField: 'financial_center_income_ref' },
          { itemType: 'empty' },
          { dataField: 'trade_account_purchase_ref' },
          { dataField: 'trade_account_sale_ref' },
        ]
      },
    ]
  }, {
    allowAdding: false,
    allowUpdating: (e) => !props.entityLock?.isLocked(false, productId),
    allowDeleting: (e) => !props.entityLock?.isLocked(false, productId),
    onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
  })
)

const onInitNewRow = (e) => {
  e.data.product_ref = productId
}

const onSaved = (e) => {
  registryStore.fetchProductAccountingsAsync(true).then(() => {
    emits('row-saved', props.data?.row.data.id, props.data?.row.key)
  })
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing.value,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow: onInitNewRow,
  onSaved: onSaved,
  onToolbarPreparing: (e) => dataGrid.useDefaultToolbar(e, {}),
  onContentReady: (e) => {
    e.component.option('editing.allowAdding', !(e.component.totalCount() > 0 || props.entityLock?.isLocked(false, productId)))
  },
  height: '100%',
})

</script>
<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
    width="100%"
  >
    <template
      #editButtonsTemplate="{ data: cellInfo }"
    >
      <edit-lock-buttons-template
        :entity-name="'Product'"
        :master-entity-id="productId"
        :cell-info="cellInfo"
        :grid="gridRef"
        :entity-lock="props.entityLock"
        :buttons="rowButtons"
      />
    </template>
    <template #customToolbarAddButtonTemplate>
      <custom-toolbar-add-button-template
        :master-entity-id="productId"
        :grid="gridRef"
        :entity-lock="props.entityLock"
      />
    </template>
  </dx-data-grid>
</template>
<style>
</style>
